<template>
  <div class="share-dialog-overlay" @click.self="$emit('close')">
    <div class="share-dialog">
      <div class="share-dialog-header">
        <h3>{{ $t('wholesaler.share.title_' + type) }}</h3>
        <button class="close-btn" @click="$emit('close')">✕</button>
      </div>

      <div class="share-dialog-body">
        <!-- 分享渠道 -->
        <div class="share-channels">
          <button class="channel-btn wechat" @click="shareViaWechat">
            <span class="channel-icon">💬</span>
            <span>{{ $t('wholesaler.share.wechat') }}</span>
          </button>
          <button class="channel-btn whatsapp" @click="shareViaWhatsapp">
            <span class="channel-icon">📱</span>
            <span>WhatsApp</span>
          </button>
          <button class="channel-btn link" @click="copyLink">
            <span class="channel-icon">🔗</span>
            <span>{{ $t('wholesaler.share.copyLink') }}</span>
          </button>
          <button class="channel-btn qrcode" @click="showQRCode = !showQRCode">
            <span class="channel-icon">📲</span>
            <span>{{ $t('wholesaler.share.qrcode') }}</span>
          </button>
        </div>

        <!-- 分享链接 -->
        <div class="share-link-section">
          <label>{{ $t('wholesaler.share.shareLink') }}</label>
          <div class="link-input-wrap">
            <input
              ref="linkInput"
              type="text"
              :value="shareUrl"
              readonly
              class="link-input"
            />
            <button class="copy-btn" @click="copyLink">{{ $t('wholesaler.share.copy') }}</button>
          </div>
        </div>

        <!-- 有效期设置 -->
        <div class="expire-setting">
          <label>{{ $t('wholesaler.share.expireTime') }}</label>
          <select v-model="expireDays" class="expire-select">
            <option :value="0">{{ $t('wholesaler.share.expire_permanent') }}</option>
            <option :value="7">{{ $t('wholesaler.share.expire_7days') }}</option>
            <option :value="14">{{ $t('wholesaler.share.expire_14days') }}</option>
            <option :value="30">{{ $t('wholesaler.share.expire_30days') }}</option>
          </select>
        </div>

        <!-- 二维码 -->
        <div v-if="showQRCode && shareUrl" class="qrcode-section">
          <div class="qrcode-wrap">
            <img :src="qrcodeUrl" :alt="$t('wholesaler.share.qrcode')" />
          </div>
          <p class="qrcode-tip">{{ $t('wholesaler.share.scanQrcode') }}</p>
        </div>

        <!-- 分享统计 -->
        <div v-if="shareData && shareData.viewCount !== undefined" class="share-stats">
          <div class="stat-item">
            <span class="stat-label">{{ $t('wholesaler.share.viewCount') }}</span>
            <span class="stat-value">{{ shareData.viewCount }}</span>
          </div>
          <div class="stat-item" v-if="type === 'product'">
            <span class="stat-label">{{ $t('wholesaler.share.orderCount') }}</span>
            <span class="stat-value">{{ shareData.orderCount || 0 }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'WholesalerShareDialog',
  props: {
    type: {
      type: String,
      required: true,
      validator: v => ['product', 'order'].includes(v)
    },
    itemId: {
      type: [Number, String],
      default: null
    },
    productIds: {
      type: Array,
      default: () => []
    },
    existingShare: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      shareUrl: '',
      shareCode: '',
      expireDays: 7,
      showQRCode: false,
      shareData: this.existingShare || null,
      creating: false
    }
  },
  computed: {
    qrcodeUrl() {
      if (!this.shareUrl) return ''
      return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(this.shareUrl)}`
    }
  },
  mounted() {
    if (this.existingShare && this.existingShare.shareUrl) {
      this.shareUrl = this.existingShare.shareUrl
      this.shareCode = this.existingShare.shareCode
    } else if (this.productIds && this.productIds.length > 0) {
      this.createBatchShare()
    } else {
      this.createShare()
    }
  },
  methods: {
    async createBatchShare() {
      if (this.creating || !this.productIds || this.productIds.length === 0) return
      this.creating = true

      try {
        const result = await api.post('/wholesaler/products/batch-share', {
          productIds: this.productIds,
          channel: 'LINK',
          expireDays: this.expireDays || null
        })

        if (result && result.shareUrl) {
          this.shareData = result
          this.shareCode = result.shareCode
          this.shareUrl = result.shareUrl
          this.$emit('created', result)
        }
      } catch (err) {
        console.error('创建批量分享失败:', err)
        this.$message.error(this.$t('wholesaler.share.createFailed') + ': ' + (err.response?.data?.message || err.message))
      } finally {
        this.creating = false
      }
    },

    async createShare() {
      if (this.creating) return
      this.creating = true

      try {
        const endpoint = this.type === 'product'
          ? `/wholesaler/products/${this.itemId}/share`
          : `/orders/${this.itemId}/share`

        const res = await api.post(endpoint, {
          channel: 'LINK',
          expireDays: this.expireDays || null
        })

        this.shareUrl = res.shareUrl
        this.shareCode = res.shareCode
        this.shareData = res

        this.$emit('created', res)
      } catch (err) {
        console.error('创建分享失败:', err)
        this.$message.error(this.$t('wholesaler.share.createFailed'))
      } finally {
        this.creating = false
      }
    },
    copyLink() {
      if (!this.shareUrl) return

      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.shareUrl).then(() => {
          this.$message.success(this.$t('wholesaler.share.linkCopied'))
        }).catch(() => {
          this.fallbackCopy()
        })
      } else {
        this.fallbackCopy()
      }
    },
    fallbackCopy() {
      const input = this.$refs.linkInput
      if (input) {
        input.select()
        try {
          document.execCommand('copy')
          this.$message.success(this.$t('wholesaler.share.linkCopied'))
        } catch (e) {
          this.$message.error(this.$t('wholesaler.share.copyFailed'))
        }
      }
    },
    shareViaWechat() {
      this.$message.info(this.$t('wholesaler.share.wechatTip'))
      this.copyLink()
    },
    shareViaWhatsapp() {
      if (!this.shareUrl) return
      const text = this.type === 'product'
        ? this.$t('wholesaler.share.whatsappProductText', { url: this.shareUrl })
        : this.$t('wholesaler.share.whatsappOrderText', { url: this.shareUrl })
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank')
    }
  }
}
</script>

<style scoped>
.share-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.share-dialog {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 420px;
  max-height: 80vh;
  overflow-y: auto;
}

.share-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.share-dialog-header h3 {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
  padding: 4px 8px;
}

.share-dialog-body {
  padding: 20px;
}

.share-channels {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

.channel-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.channel-btn:hover {
  border-color: #667eea;
  background: #f8f9ff;
}

.channel-icon {
  font-size: 24px;
}

.channel-btn span:last-child {
  font-size: 12px;
  color: #555;
}

.share-link-section {
  margin-bottom: 16px;
}

.share-link-section label,
.expire-setting label {
  display: block;
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

.link-input-wrap {
  display: flex;
  gap: 8px;
}

.link-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
  color: #333;
  background: #f9f9f9;
}

.copy-btn {
  padding: 10px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}

.expire-setting {
  margin-bottom: 20px;
}

.expire-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  background: white;
}

.qrcode-section {
  text-align: center;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 16px;
}

.qrcode-wrap img {
  width: 180px;
  height: 180px;
}

.qrcode-tip {
  margin: 12px 0 0;
  font-size: 12px;
  color: #888;
}

.share-stats {
  display: flex;
  gap: 16px;
  padding: 12px 16px;
  background: #f0f7ff;
  border-radius: 8px;
}

.stat-item {
  flex: 1;
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.stat-value {
  display: block;
  font-size: 18px;
  font-weight: 600;
  color: #667eea;
}
</style>
